import { useState, type ReactNode } from 'react';
import { ErrorCode } from '@brimo/shared';
import { Banner, Eyebrow } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { requestContactAccess } from '../api';
import type { LockedDetails, ToolKey, ToolState } from '../types';
import { Badge, EmptyBox, PaperButton, RecordAlert } from './bits';
import { Toolbar } from './Toolbar';
import styles from './record.module.css';

const LOCKED_TOOLS: Record<ToolKey, ToolState> = Object.fromEntries(
  ['call', 'whatsapp', 'email', 'appointment', 'task', 'document', 'selfDisclosure', 'note'].map((key) => [
    key,
    {
      enabled: false,
      reason: 'Kein Recht: Sie sehen, dass dieser Kontakt existiert — aber nicht seine Daten.',
    },
  ]),
) as Record<ToolKey, ToolState>;

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/** Kontaktakte I.10 „Kein Recht": the record's geometry with everything hidden but existence and owner. */
export function LockedRecord({
  id,
  details,
  message,
}: {
  id: string;
  details: LockedDetails;
  message: string;
}) {
  const [state, setState] = useState<{ pending: boolean; done: string | null; error: string | null }>({
    pending: false,
    done: null,
    error: null,
  });
  const owner = details.ownerName ?? 'einem anderen Team';
  const team = details.teamName;
  const holder = team ? `${owner} im Team ${team}` : owner;
  const holderShort = team ? `${owner}, Team ${team}` : owner;

  async function requestAccess() {
    setState({ pending: true, done: null, error: null });
    try {
      await requestContactAccess(id);
      setState({ pending: false, done: 'Ihre Zugriffsanfrage wurde gesendet.', error: null });
    } catch (error) {
      if (error instanceof ApiError && error.code === ErrorCode.CONFLICT) {
        setState({ pending: false, done: error.message, error: null });
      } else {
        setState({ pending: false, done: null, error: errorMessage(error) });
      }
    }
  }

  const invisible = <span className={`${styles.propValue} ${styles.propValueMuted}`}>nicht sichtbar</span>;
  const row = (label: string, value: ReactNode) => (
    <div className={styles.propRow}>
      <span className={styles.propLabel}>{label}</span>
      {value}
    </div>
  );

  return (
    <div className={styles.stack}>
      <RecordAlert
        tone="info"
        title={message}
        text={`Der Kontakt liegt bei ${holder}. Eigenschaften, Timeline, Dokumente und alle verknüpften Objekte bleiben verborgen. Zugriff erteilt der Org-Admin oder der Vertriebskopf des Teams.`}
        actions={
          state.done ? null : (
            <PaperButton disabled={state.pending} onClick={() => void requestAccess()}>
              Zugriff anfragen
            </PaperButton>
          )
        }
      />
      {state.done ? <Banner tone="success">{state.done}</Banner> : null}
      {state.error ? <Banner tone="danger">{state.error}</Banner> : null}

      <div className={styles.head}>
        <div className={styles.headAvatar}>{initialsOf(details.name)}</div>
        <div className={styles.headText}>
          <div className={styles.headTitleRow}>
            <h1 className={styles.headTitle}>{details.name}</h1>
            <Badge tone="info">fremder Bereich</Badge>
          </div>
          <div className={styles.headLine}>Kontaktdaten nicht sichtbar</div>
        </div>
        <div className={styles.headNote}>
          Besitzer: {owner}
          {team ? ` · Team ${team}` : ''}
        </div>
      </div>

      <div className={styles.grid}>
        <div className={styles.column}>
          <section className={styles.card} aria-label="Dokumente">
            <div className={styles.cardHead}>
              <Eyebrow size={11}>Dokumente</Eyebrow>
              <span className={styles.cardCount}>nicht sichtbar</span>
            </div>
            <EmptyBox
              title="Nicht sichtbar."
              text="Dokumente fremder Teams erscheinen nicht — auch nicht ihre Anzahl."
            />
          </section>
          <section className={styles.card} aria-label="Eigenschaften">
            <div className={styles.cardHead}>
              <Eyebrow size={11}>Eigenschaften</Eyebrow>
            </div>
            <div className={styles.propGroup}>
              <div className={styles.propGroupLabel}>Person</div>
              <div className={styles.propRows}>
                {row('Name', <span className={styles.propValue}>{details.name}</span>)}
                {row('Telefon', invisible)}
                {row('E-Mail', invisible)}
                {row('Adresse', invisible)}
              </div>
            </div>
            <div className={styles.propGroup}>
              <div className={styles.propGroupLabel}>Einordnung</div>
              <div className={styles.propRows}>
                {row('Lifecycle', invisible)}
                {row('Quelle', invisible)}
                {row('Volumen (SA)', invisible)}
              </div>
            </div>
            <div className={styles.propGroup}>
              <div className={styles.propGroupLabel}>Zuständigkeit</div>
              <div className={styles.propRows}>
                {row('Besitzer', <span className={styles.propValue}>{details.ownerName ?? '—'}</span>)}
                {team ? row('Team', <span className={styles.propValue}>{team}</span>) : null}
                {row(
                  'Ihr Scope',
                  <span className={`${styles.propValue} ${styles.propValueInfo}`}>
                    außerhalb der Führungsspanne
                  </span>,
                )}
              </div>
            </div>
          </section>
        </div>

        <div className={styles.column}>
          <Toolbar
            tools={LOCKED_TOOLS}
            allLocked
            note="Alle Aktionen sind gesperrt."
            onOpen={() => undefined}
          />
          <section className={styles.card} aria-label="Timeline">
            <div className={styles.timelineHead}>
              <Eyebrow size={11}>Timeline · Kontakt und alle zugeordneten Objekte</Eyebrow>
              <span className={styles.timelineCount}>nicht sichtbar</span>
            </div>
            <EmptyBox
              variant="big"
              title="Timeline nicht sichtbar."
              text={`Der Kontakt liegt bei ${holderShort}. Ereignisse, Kanäle und Notizen sind außerhalb dieser Führungsspanne verborgen — auch ihre Anzahl.`}
            />
          </section>
        </div>

        <div className={`${styles.column} ${styles.columnRight}`}>
          {[
            {
              label: 'Deals',
              text: 'Auch die Anzahl nicht — sonst ließen sich Bestände fremder Teams abschätzen.',
            },
            { label: 'Tickets', text: null },
            { label: 'Einheiten', text: null },
            { label: 'Portal-Vorgänge', text: null },
          ].map((group) => (
            <section
              key={group.label}
              className={styles.card}
              style={{ padding: '18px 20px' }}
              aria-label={group.label}
            >
              <div className={styles.cardHead}>
                <Eyebrow size={11}>{group.label}</Eyebrow>
                <span className={styles.cardCount}>—</span>
              </div>
              <EmptyBox variant="tight" title="Nicht sichtbar." text={group.text} />
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
